import { useMemo, useState } from "react";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import { PRODUCTS, CATEGORIES } from "@/data/products";

export default function Collection() {
  const [category, setCategory] = useState("All");
  const [activeProduct, setActiveProduct] = useState(null);

  const products = useMemo(
    () => (category === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === category)),
    [category]
  );

  return (
    <div data-testid="collection-page">
      <section className="relative pt-40 pb-16 lg:pb-20 spotlight">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp><Eyebrow>The Collection</Eyebrow></FadeUp>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-chocolate leading-[1.08] tracking-tight mt-6">
            <MaskedLine delay={0.1}>Pre-made, pre-bought,</MaskedLine>
            <MaskedLine delay={0.25} className="italic text-golddark">ready to gift.</MaskedLine>
          </h1>
          <FadeUp delay={0.35}>
            <p className="text-base md:text-lg text-chocolate/65 leading-relaxed max-w-2xl mt-7">
              Composed by our curators and wrapped in our signature style, each piece in The Collection is ready when you are. Browse freely — then enquire, and we'll confirm availability and arrange everything personally.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-24 lg:pb-32">
        <FadeUp>
          <div data-testid="collection-category-filter" className="flex flex-wrap gap-2.5 border-y border-chocolate/10 py-6">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-all duration-300 ${
                  category === c
                    ? "bg-chocolate text-cream border-chocolate"
                    : "border-chocolate/15 text-chocolate/60 hover:border-gold hover:text-chocolate"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onEnquire={setActiveProduct} />
          ))}
        </div>

        <FadeUp className="mt-16">
          <div className="bg-ivory border border-chocolate/10 p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark shrink-0">How purchasing works</p>
            <p className="text-sm text-chocolate/70 leading-relaxed">
              Every piece can be bought instantly via secure card checkout — prices include our signature wrapping. Prefer a personal touch? Send an enquiry on any piece and we'll confirm availability and arrange everything for you.
            </p>
          </div>
        </FadeUp>
      </section>

      <ProductModal product={activeProduct} onClose={() => setActiveProduct(null)} />
    </div>
  );
}
